import type { SourceId, VizSourceEntity } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { useSelector } from '../../../../Flux'
import type { State } from '../../../../Flux/Slice'
import { Actions } from '../../../../Flux/Slice'
import { useDispatch } from 'react-redux'
import { ExclamationCircleIcon } from '@heroicons/react/16/solid'
import { LoadingIndicator } from '../../../Common'
import React, { useMemo, useState } from 'react'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.ts'
import { trackVizMode } from '../../../../Services/Telemetry/index.ts'

// Combined-list value encoding: 'g:<group>' = whole-group trait COMPOSITION; 't:<group>|<name>' = filter to
// one exact trait then show the normal taxonomic breakdown; '' = none.
const groupValue = (group: string) => `g:${group}`
const traitValue = (group: string, name: string) => `t:${group}|${name}`

export default function NatureKPIPanel({ sourceId }: { sourceId: SourceId }) {
  const dispatch = useDispatch()
  const src: VizSourceEntity | null = useSelector((s: typeof State) => s.visualization.sources[sourceId] ?? null)
  const traits = useSelector((s: typeof State) => s.visualization.traits)

  const appliedGroup = src?.shared?.natureKPIs?.traitGroup ?? ''
  const appliedTraitName = src?.shared?.natureKPIs?.traitName ?? ''
  const appliedNoTraitMode = src?.shared?.natureKPIs?.noTraitMode ?? 'include'
  const [editorEnabled, setEditorEnabled] = useState(false)
  const [draftValue, setDraftValue] = useState<string | undefined>(undefined)
  const [draftNoTraitMode, setDraftNoTraitMode] = useState<'include' | 'exclude' | undefined>(undefined)

  const showEditor = appliedGroup !== '' || appliedTraitName !== '' || editorEnabled

  const appliedValue =
    appliedTraitName !== '' ? traitValue(appliedGroup, appliedTraitName) : appliedGroup !== '' ? groupValue(appliedGroup) : ''
  const selectedValue = draftValue ?? appliedValue
  const selectedNoTraitMode = draftNoTraitMode ?? appliedNoTraitMode
  const isTraitSelected = selectedValue.startsWith('t:')

  // group → sorted unique trait names, derived from the already-loaded trait rows (no extra fetch).
  const namesByGroup = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const t of traits.traits ?? []) {
      const arr = m.get(t.traitgroup) ?? []
      if (!arr.includes(t.traitname)) arr.push(t.traitname)
      m.set(t.traitgroup, arr)
    }
    for (const arr of m.values()) arr.sort((a, b) => a.localeCompare(b))
    return m
  }, [traits.traits])

  const canApply =
    selectedValue !== '' &&
    (selectedValue !== appliedValue || (!isTraitSelected && selectedNoTraitMode !== appliedNoTraitMode))

  const apply = () => {
    if (!canApply) return
    let payload: { traitGroup: string; traitName?: string; noTraitMode: 'include' | 'exclude' }
    if (selectedValue.startsWith('t:')) {
      const [group, name] = selectedValue.slice(2).split('|')
      payload = { traitGroup: group ?? '', traitName: name, noTraitMode: selectedNoTraitMode }
    } else {
      payload = { traitGroup: selectedValue.slice(2), traitName: undefined, noTraitMode: selectedNoTraitMode }
    }
    trackVizMode('nature_kpi', 'apply', {
      trait_group: payload.traitGroup,
      trait_name: payload.traitName ?? '',
      no_trait_mode: payload.noTraitMode
    })
    dispatch(Actions[visualizationSlice.name].updateNatureKpis({ sourceId, state: payload }))
    setDraftValue(undefined)
    setDraftNoTraitMode(undefined)
  }

  const remove = () => {
    dispatch(Actions[visualizationSlice.name].updateNatureKpis({ sourceId, state: undefined }))
    setDraftValue(undefined)
    setDraftNoTraitMode(undefined)
    setEditorEnabled(false)
  }

  if (traits.loadingState === 'error') {
    return (
      <div className={'flex items-center text-red-500 gap-2 text-xs font-semibold'}>
        <ExclamationCircleIcon className={'w-4 h-4 text-red-500 inline'} />
        <span>Could not load Nature KPIs</span>
      </div>
    )
  }

  if (traits.loadingState === 'initializing' || traits.loadingState === 'loading') {
    return (
      <div className={'flex items-center gap-2 text-xs font-semibold'}>
        <LoadingIndicator />
        <span>Loading Nature KPIs</span>
      </div>
    )
  }

  if (traits.traits === undefined || traits.traits.length === 0) {
    return (
      <div className={'flex items-center text-gray-500 gap-2 text-xs font-semibold'}>
        <span>No Nature KPIs available</span>
      </div>
    )
  }

  return (
    <div className={'flex flex-col justify-start gap-2'}>
      <div className={'flex items-center gap-2 font-semibold'}>
        <label className="font-semibold">Visualize Nature KPIs</label>
        <input
          type="checkbox"
          className="toggle toggle-success"
          checked={showEditor}
          onChange={() => {
            if (showEditor) {
              trackVizMode('nature_kpi', 'disable')
              remove()
            } else {
              trackVizMode('nature_kpi', 'enable')
              setEditorEnabled(true)
              setDraftValue(undefined)
              setDraftNoTraitMode(undefined)
            }
          }}
        />
      </div>

      {showEditor && (
        <div className={'flex flex-col gap-2'}>
          <div>
            <label className="font-semibold">Select a trait group or one exact trait</label>
            <select
              className="select select-bordered select-sm"
              value={selectedValue}
              onChange={(e) => setDraftValue(e.target.value)}
            >
              <option key="__placeholder" value="" disabled>
                Select a group or trait
              </option>
              {(traits.traitGroups ?? []).map((group) => (
                <React.Fragment key={group}>
                  <option value={groupValue(group)}>{group} — all (composition)</option>
                  {(namesByGroup.get(group) ?? []).map((name) => (
                    <option key={`${group}|${name}`} value={traitValue(group, name)}>
                      &nbsp;&nbsp;&nbsp;{name}
                    </option>
                  ))}
                </React.Fragment>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <label className="font-semibold">Untraited taxa</label>
              <span className="text-xs opacity-70">
                {isTraitSelected
                  ? 'Not applicable when filtering to a single trait'
                  : 'Include or exclude reads without trait annotation'}
              </span>
            </div>
            <select
              className="select select-bordered select-sm"
              value={selectedNoTraitMode}
              onChange={(e) => setDraftNoTraitMode(e.target.value as 'include' | 'exclude')}
              disabled={selectedValue === '' || isTraitSelected}
            >
              <option value="include">Include</option>
              <option value="exclude">Exclude</option>
            </select>
          </div>

          <button className="btn btn-sm btn-primary" onClick={apply} disabled={!canApply}>
            Apply
          </button>

          <span className={'text-xs italic'}>
            {isTraitSelected
              ? 'A single trait filters the whole view to OTUs carrying it, then shows the taxonomic breakdown (family → genus → species).'
              : 'A group segments the chart by its traits. “Exclude untraited” renormalizes over annotated reads only.'}
          </span>
        </div>
      )}
    </div>
  )
}
